import { isPlatformServer } from '@angular/common';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { AdminService } from '@api-client';
import { AuthTokenService, ToastService } from '@client-libs';
import {
  catchError,
  EMPTY,
  firstValueFrom,
  from,
  switchMap,
  throwError,
} from 'rxjs';
import { LOG_CONTENT } from '@client-libs';

// 로그인 페이지 리다이렉트 중복 실행 방지 플래그
let isRedirecting = false;

const redirectToSignIn = (
  authTokenService: AuthTokenService,
  router: Router,
  toastService: ToastService,
  message = '로그인 세션이 만료되었습니다. 다시 로그인해 주세요.'
) => {
  if (isRedirecting) return;
  isRedirecting = true;

  authTokenService.clearTokens();
  toastService.error(message);
  router.navigateByUrl('/sign-in', { replaceUrl: true }).then(() => {
    isRedirecting = false;
  });
};

export const HttpInterceptor: HttpInterceptorFn = (req, next) => {
  const platformId = inject(PLATFORM_ID);
  const toastService = inject(ToastService);
  const router = inject(Router);
  const authTokenService = inject(AuthTokenService);
  const adminService = inject(AdminService);

  if (isPlatformServer(platformId)) {
    return next(req);
  }

  const accessToken = authTokenService.getAccessToken();
  let request = req;

  const logContent = req.context.get(LOG_CONTENT);

  const headers: Record<string, string> = {};

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  if (logContent) {
    headers['x-log-content'] = encodeURIComponent(logContent);
  }

  if (Object.keys(headers).length > 0) {
    request = req.clone({
      setHeaders: headers,
    });
  }

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      // 토큰 없음 - 로그인 페이지로 이동
      if (error.status === 498) {
        redirectToSignIn(
          authTokenService,
          router,
          toastService,
          '로그인이 필요합니다. 로그인 후 이용해 주세요.'
        );
        return throwError(() => error);
      }

      // 토큰 만료 - 리프레시 시도
      if (error.status === 401 || error.status === 497) {
        const refreshToken = authTokenService.getRefreshToken();
        if (refreshToken) {
          return from(
            firstValueFrom(
              adminService.adminControllerRefresh({ body: { refreshToken } })
            )
          ).pipe(
            switchMap(res => {
              if (res.accessToken) {
                authTokenService.setAccessToken(res.accessToken);
                const retryReq = req.clone({
                  setHeaders: {
                    Authorization: `Bearer ${res.accessToken}`,
                  },
                });
                return next(retryReq);
              }
              return throwError(() => error);
            }),
            catchError(() => {
              redirectToSignIn(authTokenService, router, toastService);
              return EMPTY;
            })
          );
        }
        redirectToSignIn(authTokenService, router, toastService);
        return throwError(() => error);
      }

      if (error.status === 500 || error.status === 0) {
        toastService.error(
          '서버에 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'
        );
        return throwError(() => error);
      }

      if (
        error.error?.message &&
        error.status !== 401 &&
        error.status !== 497 &&
        !error.url?.includes('/admin/refresh')
      ) {
        const message = Array.isArray(error.error.message)
          ? error.error.message[0]
          : error.error.message;
        toastService.error(message);
      }

      return throwError(() => error);
    })
  );
};
